import { Link, useLocation } from 'react-router-dom';
import { ConfigResourcePage } from '../components/ConfigResourcePage';
import { configApi } from '../api/config';
import { mappingsApi } from '../api/mappings';
import { templatesApi } from '../api/templates';
import { validationRulesApi } from '../api/validationRules';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { DisabledActionButton } from '../components/DisabledActionButton';
import { redactSensitiveValue } from '../components/PayloadViewer';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePermission, type Permission } from '../hooks/usePermission';
import { useEffect, useState } from 'react';
import { MARITIME_DOCUMENT_TYPE_OPTIONS } from '../types/api';
import type {
  As2TransportProfileRecord,
  ConnectorDefinitionRecord,
  ConnectorInstanceRecord,
  DestinationPolicyResult,
  DocumentType,
  GovernanceLifecycleState,
  InboundPolicyRecord,
  MappingRecord,
  MappingVersionRecord,
  PartnerAuthTypeMetadata,
  PartnerConnectivityValidationRecord,
  PartnerDestinationMetadata,
  PartnerMetadata,
  PartnerRecord,
  PartnerConfigurationVersionRecord,
  PartnerRuntimeDestinationRecord,
  PartnerRuntimeProfileRecord,
  TemplateRecord,
  TemplateVersionRecord,
  ValidationRuleRecord,
  ValidationRuleVersionRecord,
} from '../types/api';

const documentTypeOptions = MARITIME_DOCUMENT_TYPE_OPTIONS.map((value) => ({ label: value, value }));

const VERSIONED_CATALOG_PAGE_SIZE = 100;

// ─── Partner form types ───────────────────────────────────────────────────────

interface PartnerDraft {
  version?: number;
  name: string;
  legalName: string;
  code: string;
  protocol: 'EDI' | 'XML';
  environmentProfile: 'DEV' | 'TEST' | 'UAT' | 'PROD';
  operationalStatus: 'ONBOARDING' | 'OPERATIONAL' | 'PAUSED' | 'OFFBOARDED';
  isActive: boolean;
  supportedProtocols: string[];
  supportedMessageTypes: string[];
  throughputRequestsPerMinute: string;
  throughputPayloadMaxMb: string;
  contactsJson: string;
  taxIdentifiersJson: string;
  slaProfileJson: string;
  securityProfileJson: string;
  settingsJson: string;
  inboundCapabilitiesJson: string;
  outboundCapabilitiesJson: string;
  runtimeProfile: PartnerRuntimeProfileRecord;
  runtimeSecretRefsJson: Record<PartnerRuntimeDestinationRecord['code'], string>;
  runtimeConfigurationJson: Record<PartnerRuntimeDestinationRecord['code'], string>;
}

interface As2ProfileDraft {
  id?: string;
  environmentProfile: 'DEV' | 'TEST' | 'UAT' | 'PROD';
  code: string;
  profileName: string;
  as2Identifier: string;
  outboundEndpoint: string;
  mdnMode: 'NONE' | 'SYNCHRONOUS' | 'ASYNCHRONOUS';
  outboundSigningEnabled: boolean;
  outboundEncryptionEnabled: boolean;
  signedMdnRequired: boolean;
}

const RUNTIME_MESSAGE_FAMILIES = [
  'PMIS_TO_CATOS_BERTH_PLAN_REQUEST', 'CATOS_TO_PMIS_BERTH_PLAN_UPDATE', 'DUM_CUSDEC', 'CUSDEC_ML', 'ETAT_CHARGEMENT', 'TRYPTIQUE',
];

const RUNTIME_MESSAGE_FAMILY_LABELS: Record<string, string> = {
  PMIS_TO_CATOS_BERTH_PLAN_REQUEST: 'PMIS to CATOS — Berth Plan Request',
  CATOS_TO_PMIS_BERTH_PLAN_UPDATE: 'CATOS to PMIS — Berth Plan Update',
};

const RUNTIME_DESTINATIONS: PartnerRuntimeDestinationRecord['code'][] = [
  'CATOS_PMIS001', 'PMIS_PMIS002', 'DUM_API', 'ADII_AS2',
];

// Fallback metadata mirroring the backend's static PARTNER_ADMINISTRATION_METADATA
// (apps/integration/backend/src/application/partners/partner-administration-metadata.ts),
// used only until GET /partners/metadata resolves so the form never flashes an incorrect state.
const FALLBACK_DESTINATION_METADATA: PartnerDestinationMetadata[] = [
  { code: 'CATOS_PMIS001', label: 'CATOS PMIS001', runtimeWired: true, selectable: true, supportedMessageFamilies: ['PMIS_TO_CATOS_BERTH_PLAN_REQUEST'] },
  { code: 'PMIS_PMIS002', label: 'PMIS PMIS002', runtimeWired: true, selectable: true, supportedMessageFamilies: ['CATOS_TO_PMIS_BERTH_PLAN_UPDATE'] },
  { code: 'DUM_API', label: 'DUM API', runtimeWired: true, selectable: true, supportedMessageFamilies: ['DUM_CUSDEC'] },
  { code: 'ADII_AS2', label: 'ADII AS2', runtimeWired: false, selectable: false, supportedMessageFamilies: [], disabledReason: 'Partner runtime wiring is not implemented.' },
];

const FALLBACK_AUTH_TYPE_METADATA: PartnerAuthTypeMetadata[] = [
  { code: 'NONE', label: 'No authentication', selectable: true, implemented: true, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [] },
  { code: 'BASIC', label: 'HTTP Basic', selectable: true, implemented: true, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [] },
  { code: 'BEARER', label: 'Bearer token', selectable: true, implemented: true, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [] },
  { code: 'API_KEY', label: 'API key', selectable: true, implemented: true, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [] },
  { code: 'JWT_LOGIN', label: 'JWT login', selectable: true, implemented: true, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [] },
  { code: 'OAUTH2', label: 'OAuth 2', selectable: false, implemented: false, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [], disabledReason: 'No registered berth-plan runtime adapter.' },
  { code: 'AS2', label: 'AS2', selectable: false, implemented: false, supportedMessageFamilies: [], requiredSecretReferences: [], requiredFields: [], disabledReason: 'Use the separately managed AS2 gateway.' },
];

function defaultRuntimeProfile(): PartnerRuntimeProfileRecord {
  return {
    messageFamilies: RUNTIME_MESSAGE_FAMILIES.map((code) => ({ code, enabled: false })),
    destinations: RUNTIME_DESTINATIONS.map((code) => ({
      code,
      endpoint: '',
      authenticationType: code === 'CATOS_PMIS001' ? 'BASIC' : code === 'ADII_AS2' ? 'AS2' : 'NONE',
      timeoutMs: 10_000,
      maxAttempts: 2,
      backoffMs: 250,
      secretRefs: [],
      configuration: {},
    })),
  };
}

function normalizedRuntimeProfile(profile?: PartnerRuntimeProfileRecord): PartnerRuntimeProfileRecord {
  const defaults = defaultRuntimeProfile();
  return {
    messageFamilies: defaults.messageFamilies.map((fallback) =>
      profile?.messageFamilies.find((item) => item.code === fallback.code) ?? fallback,
    ),
    destinations: defaults.destinations.map((fallback) =>
      profile?.destinations.find((item) => item.code === fallback.code) ?? fallback,
    ),
  };
}

function defaultPartnerDraft(): PartnerDraft {
  return {
    name: '', legalName: '', code: '',
    protocol: 'EDI', environmentProfile: 'DEV',
    operationalStatus: 'ONBOARDING', isActive: true,
    supportedProtocols: ['EDI'], supportedMessageTypes: [],
    throughputRequestsPerMinute: '0', throughputPayloadMaxMb: '0',
    contactsJson: '[]', taxIdentifiersJson: '{}',
    slaProfileJson: '{}', securityProfileJson: '{}', settingsJson: '{}',
    inboundCapabilitiesJson: '[]', outboundCapabilitiesJson: '[]',
    runtimeProfile: defaultRuntimeProfile(),
    runtimeSecretRefsJson: Object.fromEntries(RUNTIME_DESTINATIONS.map((code) => [code, '[]'])) as PartnerDraft['runtimeSecretRefsJson'],
    runtimeConfigurationJson: Object.fromEntries(RUNTIME_DESTINATIONS.map((code) => [code, '{}'])) as PartnerDraft['runtimeConfigurationJson'],
  };
}

function parseJsonPreview(text: string): unknown {
  try { return JSON.parse(text || '{}'); } catch { return text; }
}

function as2ProfileSaveDisabledReason(form: As2ProfileDraft | null, saving: boolean): string | null {
  if (saving) return 'A save is already in progress.';
  if (!form) return 'Open a profile to edit before saving.';
  if (!form.code.trim()) return 'Gateway profile code is required.';
  if (!form.profileName.trim()) return 'Profile name is required.';
  if (!form.as2Identifier.trim()) return 'Remote AS2 identifier is required.';
  if (!form.outboundEndpoint.trim()) return 'Remote AS2 HTTPS endpoint is required.';
  return null;
}

function partnerToForm(partner: PartnerRecord): PartnerDraft {
  const limits = partner.throughputLimits as { requestsPerMinute?: number; payloadMaxMb?: number } | undefined;
  return {
    version: partner.version,
    name: partner.name,
    legalName: partner.legalName ?? '',
    code: partner.code,
    protocol: (partner.protocol as 'EDI' | 'XML') ?? 'EDI',
    environmentProfile: (partner.environmentProfile as 'DEV' | 'TEST' | 'UAT' | 'PROD') ?? 'DEV',
    operationalStatus: (partner.operationalStatus as 'ONBOARDING' | 'OPERATIONAL' | 'PAUSED' | 'OFFBOARDED') ?? 'ONBOARDING',
    isActive: partner.isActive ?? true,
    supportedProtocols: Array.isArray(partner.supportedProtocols) ? (partner.supportedProtocols as string[]) : [],
    supportedMessageTypes: Array.isArray(partner.supportedMessageTypes) ? (partner.supportedMessageTypes as string[]) : [],
    throughputRequestsPerMinute: String(limits?.requestsPerMinute ?? 0),
    throughputPayloadMaxMb: String(limits?.payloadMaxMb ?? 0),
    contactsJson: JSON.stringify(partner.contacts ?? [], null, 2),
    taxIdentifiersJson: JSON.stringify(partner.taxIdentifiers ?? {}, null, 2),
    slaProfileJson: JSON.stringify(partner.slaProfile ?? {}, null, 2),
    securityProfileJson: JSON.stringify(partner.securityProfile ?? {}, null, 2),
    settingsJson: JSON.stringify(partner.settings ?? {}, null, 2),
    inboundCapabilitiesJson: JSON.stringify(partner.inboundCapabilities ?? [], null, 2),
    outboundCapabilitiesJson: JSON.stringify(partner.outboundCapabilities ?? [], null, 2),
    runtimeProfile: normalizedRuntimeProfile(partner.runtimeProfile),
    runtimeSecretRefsJson: Object.fromEntries(normalizedRuntimeProfile(partner.runtimeProfile).destinations.map(
      (destination) => [destination.code, JSON.stringify(destination.secretRefs, null, 2)],
    )) as PartnerDraft['runtimeSecretRefsJson'],
    runtimeConfigurationJson: Object.fromEntries(normalizedRuntimeProfile(partner.runtimeProfile).destinations.map(
      (destination) => [destination.code, JSON.stringify(destination.configuration ?? {}, null, 2)],
    )) as PartnerDraft['runtimeConfigurationJson'],
  };
}

function formToPartnerPayload(form: PartnerDraft): Omit<PartnerRecord, 'id' | 'createdAt' | 'updatedAt'> {
  const safeJson = (text: string, fallback: unknown) => {
    try { return JSON.parse(text || JSON.stringify(fallback)) as unknown; } catch { return fallback; }
  };
  return {
    version: form.version,
    name: form.name.trim(),
    legalName: form.legalName.trim(),
    code: form.code.trim(),
    protocol: form.protocol,
    environmentProfile: form.environmentProfile,
    operationalStatus: form.operationalStatus,
    isActive: form.isActive,
    supportedProtocols: form.supportedProtocols,
    supportedMessageTypes: form.supportedMessageTypes as DocumentType[],
    throughputLimits: {
      requestsPerMinute: Number(form.throughputRequestsPerMinute) || 0,
      payloadMaxMb: Number(form.throughputPayloadMaxMb) || 0,
    },
    contacts: safeJson(form.contactsJson, []) as Record<string, unknown>[],
    taxIdentifiers: safeJson(form.taxIdentifiersJson, {}) as Record<string, unknown>,
    slaProfile: safeJson(form.slaProfileJson, {}) as Record<string, unknown>,
    securityProfile: safeJson(form.securityProfileJson, {}) as Record<string, unknown>,
    settings: safeJson(form.settingsJson, {}) as Record<string, unknown>,
    inboundCapabilities: safeJson(form.inboundCapabilitiesJson, []) as string[],
    outboundCapabilities: safeJson(form.outboundCapabilitiesJson, []) as string[],
    runtimeProfile: {
      messageFamilies: form.runtimeProfile.messageFamilies,
      destinations: form.runtimeProfile.destinations
        .filter((destination) => destination.endpoint.trim())
        .map((destination) => ({
          ...destination,
          secretRefs: safeJson(form.runtimeSecretRefsJson[destination.code], []) as PartnerRuntimeDestinationRecord['secretRefs'],
          configuration: safeJson(form.runtimeConfigurationJson[destination.code], {}) as Record<string, unknown>,
        })),
    },
  };
}

function defaultAs2ProfileDraft(env = 'DEV'): As2ProfileDraft {
  return {
    environmentProfile: (env as 'DEV' | 'TEST' | 'UAT' | 'PROD'),
    code: '', profileName: '', as2Identifier: '', outboundEndpoint: '',
    mdnMode: 'ASYNCHRONOUS',
    outboundSigningEnabled: false, outboundEncryptionEnabled: false, signedMdnRequired: false,
  };
}

const PARTNER_PROTOCOLS = ['EDI', 'XML', 'REST', 'WEBHOOK', 'SFTP', 'KAFKA', 'SOAP', 'MANUAL'];

// ─── Partners page (Fix 5: structured form; Fix 4: AS2 panel) ────────────────

export function PartnersPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<PartnerDraft>(defaultPartnerDraft);
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  // AS2 profile state lifted to page level to avoid table-layout nesting (Fix 4)
  const [as2PartnerId, setAs2PartnerId] = useState<string | null>(null);
  const [as2Profiles, setAs2Profiles] = useState<As2TransportProfileRecord[]>([]);
  const [as2Form, setAs2Form] = useState<As2ProfileDraft | null>(null);
  const [as2Saving, setAs2Saving] = useState(false);
  const [as2Error, setAs2Error] = useState<string | null>(null);
  const [versionPartnerId, setVersionPartnerId] = useState<string | null>(null);
  const [versions, setVersions] = useState<PartnerConfigurationVersionRecord[]>([]);
  const [rollbackReason, setRollbackReason] = useState('');
  const [versionError, setVersionError] = useState<string | null>(null);
  const [versionBusy, setVersionBusy] = useState(false);
  const [securityProfileRevealed, setSecurityProfileRevealed] = useState(false);
  const canRevealSensitive = usePermission('integration.payload.revealSensitive');

  const { data, isLoading, error, refresh } = useAsyncData(
    () => configApi.partners.list({ page: 1, pageSize: 100 }),
    [],
  );
  const { data: metadata } = useAsyncData<PartnerMetadata>(
    () => configApi.partners.metadata(),
    [],
  );
  const authTypeOptions = metadata?.authenticationTypes ?? FALLBACK_AUTH_TYPE_METADATA;
  const destinationMetadata = metadata?.destinations ?? FALLBACK_DESTINATION_METADATA;
  const partners = data?.data ?? [];
  const selectedPartner = partners.find((p) => p.id === selectedId) ?? null;
  const as2Partner = partners.find((p) => p.id === as2PartnerId) ?? null;

  function editPartner(partner: PartnerRecord) {
    setSelectedId(partner.id);
    setForm(partnerToForm(partner));
    setJsonErrors({});
    setSubmitError(null);
    setSecurityProfileRevealed(false);
  }

  function resetForm() {
    setSelectedId(null);
    setForm(defaultPartnerDraft());
    setJsonErrors({});
    setSubmitError(null);
    setSecurityProfileRevealed(false);
  }

  function patchForm(patch: Partial<PartnerDraft>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function toggleProtocol(proto: string) {
    setForm((current) => ({
      ...current,
      supportedProtocols: current.supportedProtocols.includes(proto)
        ? current.supportedProtocols.filter((p) => p !== proto)
        : [...current.supportedProtocols, proto],
    }));
  }

  function toggleMessageType(type: string) {
    setForm((current) => ({
      ...current,
      supportedMessageTypes: current.supportedMessageTypes.includes(type)
        ? current.supportedMessageTypes.filter((t) => t !== type)
        : [...current.supportedMessageTypes, type],
    }));
  }

  function toggleRuntimeFamily(code: string) {
    setForm((current) => ({
      ...current,
      runtimeProfile: {
        ...current.runtimeProfile,
        messageFamilies: current.runtimeProfile.messageFamilies.map((family) =>
          family.code === code ? { ...family, enabled: !family.enabled } : family,
        ),
      },
    }));
  }

  function patchRuntimeDestination(
    code: PartnerRuntimeDestinationRecord['code'],
    patch: Partial<PartnerRuntimeDestinationRecord>,
  ) {
    setForm((current) => ({
      ...current,
      runtimeProfile: {
        ...current.runtimeProfile,
        destinations: current.runtimeProfile.destinations.map((destination) =>
          destination.code === code ? { ...destination, ...patch } : destination,
        ),
      },
    }));
  }

  function validateJson(key: keyof PartnerDraft, value: string) {
    try {
      JSON.parse(value || '[]');
      setJsonErrors((current) => { const next = { ...current }; delete next[key as string]; return next; });
    } catch {
      setJsonErrors((current) => ({ ...current, [key as string]: 'Invalid JSON' }));
    }
  }

  async function openAs2Panel(partner: PartnerRecord) {
    try {
      const profiles = await configApi.partners.listAs2Profiles(partner.id);
      setAs2Profiles(profiles);
      setAs2PartnerId(partner.id);
      setAs2Form(null);
      setAs2Error(null);
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to load AS2 profiles.');
    }
  }

  async function openVersions(partner: PartnerRecord) {
    setVersionBusy(true);
    setVersionError(null);
    try {
      setVersions(await configApi.partners.versions(partner.id));
      setVersionPartnerId(partner.id);
      setRollbackReason('');
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to load configuration versions.');
    } finally {
      setVersionBusy(false);
    }
  }

  async function rollbackTo(version: number) {
    const partner = partners.find((item) => item.id === versionPartnerId);
    if (!partner || !rollbackReason.trim()) {
      setVersionError('A rollback reason is required.');
      return;
    }
    setVersionBusy(true);
    setVersionError(null);
    try {
      await configApi.partners.rollback(partner.id, version, partner.version, rollbackReason.trim());
      await refresh();
      setVersions(await configApi.partners.versions(partner.id));
      setRollbackReason('');
    } catch (caught) {
      setVersionError(caught instanceof Error ? caught.message : 'Rollback failed.');
    } finally {
      setVersionBusy(false);
    }
  }

  async function saveAs2Profile() {
    if (!as2Form || !as2PartnerId) return;
    setAs2Saving(true);
    setAs2Error(null);
    try {
      await configApi.partners.saveAs2Profile(as2PartnerId, {
        id: as2Form.id,
        integrationPartnerId: as2PartnerId,
        ...as2Form,
      });
      const profiles = await configApi.partners.listAs2Profiles(as2PartnerId);
      setAs2Profiles(profiles);
      setAs2Form(null);
    } catch (caught) {
      setAs2Error(caught instanceof Error ? caught.message : 'AS2 profile save failed.');
    } finally {
      setAs2Saving(false);
    }
  }

  function requestSaveAs2Profile() {
    if (as2ProfileSaveDisabledReason(as2Form, as2Saving) || !as2Form) return;
    setConfirm({
      title: as2Form.id ? 'Update AS2 transport profile' : 'Create AS2 transport profile',
      message: `This changes the live AS2 gateway configuration (${as2Form.environmentProfile}) used to deliver messages to ${as2Partner?.name ?? 'this partner'}. A misconfigured endpoint or identifier can break partner connectivity.`,
      confirmLabel: as2Form.id ? 'Update profile' : 'Create profile',
      risk: 'high',
      requireText: 'SAVE',
      onConfirm: () => saveAs2Profile(),
    });
  }

  async function submitPartner(event: React.FormEvent) {
    event.preventDefault();
    if (!form.name.trim() || !form.code.trim()) { setSubmitError('Name and code are required.'); return; }
    const jsonFields: Array<keyof PartnerDraft> = [
      'contactsJson', 'taxIdentifiersJson', 'slaProfileJson',
      'securityProfileJson', 'settingsJson', 'inboundCapabilitiesJson', 'outboundCapabilitiesJson',
    ];
    const errors: Record<string, string> = {};
    for (const key of jsonFields) {
      try { JSON.parse((form[key] as string) || '{}'); } catch { errors[key as string] = 'Invalid JSON'; }
    }
    for (const code of RUNTIME_DESTINATIONS) {
      try { JSON.parse(form.runtimeSecretRefsJson[code] || '[]'); } catch { errors[`runtimeSecretRefsJson.${code}`] = 'Invalid JSON'; }
      try { JSON.parse(form.runtimeConfigurationJson[code] || '{}'); } catch { errors[`runtimeConfigurationJson.${code}`] = 'Invalid JSON'; }
    }
    if (Object.keys(errors).length > 0) { setJsonErrors(errors); setSubmitError('Fix JSON errors before saving.'); return; }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPartnerPayload(form);
      if (selectedId) await configApi.partners.update(selectedId, toPartnerUpdatePayload(payload as PartnerRecord));
      else await configApi.partners.create(toPartnerCreatePayload(payload as PartnerRecord));
      resetForm();
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to save partner.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="page-stack">
      <div className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Partner configurations</h2>
              <p>Create, govern, and validate enterprise partner onboarding profiles.</p>
            </div>
          </div>
          {isLoading ? <LoadingState /> : null}
          {error ? <ErrorState message={error} onRetry={refresh} /> : null}
          {!isLoading && partners.length === 0 ? <EmptyState label="No partners configured." /> : null}
          {partners.length ? (
            <table className="data-table">
              <thead><tr><th>Name</th><th>Code</th><th>Environment</th><th>Status</th><th /></tr></thead>
              <tbody>{partners.map((partner) => (
                <tr key={partner.id}>
                  <td>{partner.name}</td>
                  <td className="mono">{partner.code}</td>
                  <td>{partner.environmentProfile}</td>
                  <td><StatusBadge value={partner.operationalStatus ?? (partner.isActive ? 'ACTIVE' : 'INACTIVE')} /></td>
                  <td className="actions-cell">
                    <PartnerLifecycleActions partner={partner} onChanged={refresh} onManageAs2={() => void openAs2Panel(partner)} />
                    <button type="button" onClick={() => void openVersions(partner)}>Versions</button>
                    <button type="button" onClick={() => editPartner(partner)}>Edit</button>
                    <button type="button" className="danger-link" onClick={() => setConfirm({
                      title: 'Delete partner',
                      message: `Delete ${partner.name}. This removes the partner and all linked configuration.`,
                      confirmLabel: 'Delete partner', risk: 'high', requireText: 'DELETE',
                      onConfirm: async () => { await configApi.partners.remove(partner.id); await refresh(); },
                    })}>Delete</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          ) : null}
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2>{selectedId ? `Edit: ${selectedPartner?.name ?? ''}` : 'Create partner'}</h2>
            {selectedId ? <button type="button" className="ghost-button" onClick={resetForm}>New</button> : null}
          </div>
          <form className="form-grid" onSubmit={submitPartner}>
            <label><span>Name</span><input required value={form.name} onChange={(e) => patchForm({ name: e.target.value })} /></label>
            <label><span>Code</span><input required value={form.code} onChange={(e) => patchForm({ code: e.target.value })} placeholder="e.g. ACME_CORP" /></label>
            <label className="full-width"><span>Legal name</span><input value={form.legalName} onChange={(e) => patchForm({ legalName: e.target.value })} /></label>
            <label><span>Primary protocol</span>
              <select value={form.protocol} onChange={(e) => patchForm({ protocol: e.target.value as 'EDI' | 'XML' })}>
                <option value="EDI">EDI</option><option value="XML">XML</option>
              </select>
            </label>
            <label><span>Environment</span>
              <select value={form.environmentProfile} onChange={(e) => patchForm({ environmentProfile: e.target.value as 'DEV' | 'TEST' | 'UAT' | 'PROD' })}>
                {['DEV', 'TEST', 'UAT', 'PROD'].map((env) => <option key={env}>{env}</option>)}
              </select>
            </label>
            <label><span>Operational status</span>
              <select value={form.operationalStatus} onChange={(e) => patchForm({ operationalStatus: e.target.value as 'ONBOARDING' | 'OPERATIONAL' | 'PAUSED' | 'OFFBOARDED' })}>
                {['ONBOARDING', 'OPERATIONAL', 'PAUSED', 'OFFBOARDED'].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="checkbox-field full-width"><input type="checkbox" checked={form.isActive} onChange={(e) => patchForm({ isActive: e.target.checked })} /><span>Partner is active</span></label>

            <div className="full-width">
              <span className="field-label">Supported protocols</span>
              <div className="choice-group">
                {PARTNER_PROTOCOLS.map((proto) => (
                  <label key={proto} className="inline-check">
                    <input type="checkbox" checked={form.supportedProtocols.includes(proto)} onChange={() => toggleProtocol(proto)} />{proto}
                  </label>
                ))}
              </div>
            </div>

            <div className="full-width">
              <span className="field-label">Supported message types</span>
              <div className="choice-group">
                {MARITIME_DOCUMENT_TYPE_OPTIONS.map((type) => (
                  <label key={type} className="inline-check">
                    <input type="checkbox" checked={form.supportedMessageTypes.includes(type)} onChange={() => toggleMessageType(type)} />{type}
                  </label>
                ))}
              </div>
            </div>

            <label><span>Throughput: requests / min</span><input type="number" min={0} value={form.throughputRequestsPerMinute} onChange={(e) => patchForm({ throughputRequestsPerMinute: e.target.value })} /></label>
            <label><span>Throughput: max payload MB</span><input type="number" min={0} value={form.throughputPayloadMaxMb} onChange={(e) => patchForm({ throughputPayloadMaxMb: e.target.value })} /></label>

            <fieldset className="full-width">
              <legend>Enabled runtime message families</legend>
              <p className="field-hint">Only an approved partner version is used by the runtime.</p>
              <div className="choice-group">
                {form.runtimeProfile.messageFamilies.map((family) => (
                  <label key={family.code} className="inline-check">
                    <input type="checkbox" checked={family.enabled} onChange={() => toggleRuntimeFamily(family.code)} />
                    {RUNTIME_MESSAGE_FAMILY_LABELS[family.code] ?? family.code}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="full-width">
              <h3>Runtime destinations</h3>
              <p className="field-hint">
                Store URLs and policies here. Store credentials in Vault; this form accepts references only,
                using references such as <code>kv/data/oss/partners/catos#password</code>.
              </p>
              {form.runtimeProfile.destinations.map((destination) => {
                const destMeta = destinationMetadata.find((item) => item.code === destination.code);
                const destinationDisabled = destMeta ? !destMeta.selectable : false;
                return (
                <fieldset key={destination.code} className="runtime-destination-card">
                  <legend>{destMeta?.label ?? destination.code}</legend>
                  {destinationDisabled ? (
                    <p className="field-hint field-warning">
                      Not available: {destMeta?.disabledReason ?? 'This destination has no runtime wiring.'}
                    </p>
                  ) : null}
                  <div className="form-grid">
                    <label className="full-width"><span>HTTPS endpoint</span>
                      <input
                        type="url"
                        disabled={destinationDisabled}
                        value={destination.endpoint}
                        onChange={(event) => patchRuntimeDestination(destination.code, { endpoint: event.target.value })}
                        placeholder="Leave blank when this destination is not configured"
                      />
                    </label>
                    <label><span>Authentication</span>
                      <select
                        disabled={destinationDisabled}
                        value={destination.authenticationType}
                        onChange={(event) => patchRuntimeDestination(destination.code, {
                          authenticationType: event.target.value as PartnerRuntimeDestinationRecord['authenticationType'],
                        })}
                      >
                        {authTypeOptions.map((option) => (
                          <option key={option.code} value={option.code} disabled={!option.selectable}>
                            {option.label}{option.selectable ? '' : ' — not available'}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const selectedAuth = authTypeOptions.find((option) => option.code === destination.authenticationType);
                        return selectedAuth && !selectedAuth.selectable ? (
                          <span className="field-hint field-warning">{selectedAuth.disabledReason ?? 'This authentication type is not implemented.'}</span>
                        ) : null;
                      })()}
                    </label>
                    <label><span>Timeout (ms)</span>
                      <input type="number" min={100} max={300000} disabled={destinationDisabled} value={destination.timeoutMs} onChange={(event) => patchRuntimeDestination(destination.code, { timeoutMs: Number(event.target.value) })} />
                    </label>
                    <label><span>Maximum attempts</span>
                      <input type="number" min={1} max={10} disabled={destinationDisabled} value={destination.maxAttempts} onChange={(event) => patchRuntimeDestination(destination.code, { maxAttempts: Number(event.target.value) })} />
                    </label>
                    <label><span>Retry backoff (ms)</span>
                      <input type="number" min={0} max={300000} disabled={destinationDisabled} value={destination.backoffMs} onChange={(event) => patchRuntimeDestination(destination.code, { backoffMs: Number(event.target.value) })} />
                    </label>
                    <label className="full-width"><span>Vault secret references (JSON array)</span>
                      <textarea
                        rows={4}
                        disabled={destinationDisabled}
                        value={form.runtimeSecretRefsJson[destination.code]}
                        onChange={(event) => patchForm({
                          runtimeSecretRefsJson: { ...form.runtimeSecretRefsJson, [destination.code]: event.target.value },
                        })}
                      />
                      {jsonErrors[`runtimeSecretRefsJson.${destination.code}`] ? <span className="field-error">Invalid JSON</span> : null}
                    </label>
                    <label className="full-width"><span>Non-secret configuration (JSON)</span>
                      <textarea
                        rows={3}
                        disabled={destinationDisabled}
                        value={form.runtimeConfigurationJson[destination.code]}
                        onChange={(event) => patchForm({
                          runtimeConfigurationJson: { ...form.runtimeConfigurationJson, [destination.code]: event.target.value },
                        })}
                      />
                      {jsonErrors[`runtimeConfigurationJson.${destination.code}`] ? <span className="field-error">Invalid JSON</span> : null}
                    </label>
                  </div>
                </fieldset>
                );
              })}
            </div>

            {([
              ['contactsJson', 'Contacts', '(JSON array of { name, email, role } objects)'],
              ['inboundCapabilitiesJson', 'Inbound capabilities', '(JSON string array)'],
              ['outboundCapabilitiesJson', 'Outbound capabilities', '(JSON string array)'],
              ['taxIdentifiersJson', 'Tax identifiers', ''],
              ['slaProfileJson', 'SLA profile', ''],
              ['settingsJson', 'Settings', ''],
            ] as const).map(([key, label, hint]) => (
              <label key={key} className="full-width">
                <span>{label}{hint ? <small> {hint}</small> : null}</span>
                <textarea rows={3} value={form[key]} onChange={(e) => patchForm({ [key]: e.target.value })} onBlur={(e) => validateJson(key, e.target.value)} />
                {jsonErrors[key] ? <span className="field-error">{jsonErrors[key]}</span> : null}
              </label>
            ))}

            <label className="full-width">
              <span>Security profile</span>
              <textarea
                rows={3}
                value={canRevealSensitive && securityProfileRevealed
                  ? form.securityProfileJson
                  : JSON.stringify(redactSensitiveValue(parseJsonPreview(form.securityProfileJson)), null, 2)}
                readOnly={!(canRevealSensitive && securityProfileRevealed)}
                onChange={(e) => patchForm({ securityProfileJson: e.target.value })}
                onBlur={(e) => validateJson('securityProfileJson', e.target.value)}
              />
              {jsonErrors.securityProfileJson ? <span className="field-error">{jsonErrors.securityProfileJson}</span> : null}
              {canRevealSensitive ? (
                <button type="button" className="ghost-button" onClick={() => setSecurityProfileRevealed((current) => !current)}>
                  {securityProfileRevealed ? 'Hide raw / lock field' : 'Reveal raw to edit'}
                </button>
              ) : (
                <span className="field-hint">Redacted — editing raw values needs reveal-sensitive permission.</span>
              )}
            </label>

            {submitError ? <div className="form-error full-width">{submitError}</div> : null}
            <div className="form-actions full-width">
              <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : selectedId ? 'Update partner' : 'Create partner'}</button>
            </div>
          </form>
        </section>
      </div>

      {selectedId && selectedPartner ? (
        <PartnerOperationsPanel partner={selectedPartner} destinationMetadata={destinationMetadata} />
      ) : null}

      {/* Fix 4: AS2 profile panel — lives at page level to avoid table-layout nesting */}
      {as2PartnerId && as2Partner ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>AS2 transport profiles — {as2Partner.name}</h2>
              <p>Configure per-environment AS2 gateway endpoints, identifiers, and MDN settings.</p>
            </div>
            <div className="panel-actions">
              {!as2Form ? (
                <button type="button" onClick={() => setAs2Form(defaultAs2ProfileDraft(as2Partner.environmentProfile ?? 'DEV'))}>New profile</button>
              ) : null}
              <button type="button" className="ghost-button" onClick={() => { setAs2PartnerId(null); setAs2Form(null); setAs2Error(null); }}>Close</button>
            </div>
          </div>
          {as2Form ? (
            <div className="form-grid">
              <label><span>Environment</span>
                <select value={as2Form.environmentProfile} onChange={(e) => setAs2Form((f) => f ? { ...f, environmentProfile: e.target.value as 'DEV' | 'TEST' | 'UAT' | 'PROD' } : f)}>
                  {['DEV', 'TEST', 'UAT', 'PROD'].map((env) => <option key={env}>{env}</option>)}
                </select>
              </label>
              <label><span>Gateway profile code</span><input value={as2Form.code} onChange={(e) => setAs2Form((f) => f ? { ...f, code: e.target.value } : f)} /></label>
              <label><span>Profile name</span><input value={as2Form.profileName} onChange={(e) => setAs2Form((f) => f ? { ...f, profileName: e.target.value } : f)} /></label>
              <label><span>Remote AS2 identifier</span><input value={as2Form.as2Identifier} onChange={(e) => setAs2Form((f) => f ? { ...f, as2Identifier: e.target.value } : f)} /></label>
              <label className="full-width"><span>Remote AS2 HTTPS endpoint</span><input value={as2Form.outboundEndpoint} onChange={(e) => setAs2Form((f) => f ? { ...f, outboundEndpoint: e.target.value } : f)} /></label>
              <label><span>MDN mode</span>
                <select value={as2Form.mdnMode} onChange={(e) => setAs2Form((f) => f ? { ...f, mdnMode: e.target.value as 'NONE' | 'SYNCHRONOUS' | 'ASYNCHRONOUS' } : f)}>
                  <option value="NONE">None</option>
                  <option value="SYNCHRONOUS">Synchronous</option>
                  <option value="ASYNCHRONOUS">Asynchronous</option>
                </select>
              </label>
              <label className="checkbox-field"><input type="checkbox" checked={as2Form.outboundSigningEnabled} onChange={(e) => setAs2Form((f) => f ? { ...f, outboundSigningEnabled: e.target.checked } : f)} /><span>Outbound signing</span></label>
              <label className="checkbox-field"><input type="checkbox" checked={as2Form.outboundEncryptionEnabled} onChange={(e) => setAs2Form((f) => f ? { ...f, outboundEncryptionEnabled: e.target.checked } : f)} /><span>Outbound encryption</span></label>
              <label className="checkbox-field"><input type="checkbox" checked={as2Form.signedMdnRequired} onChange={(e) => setAs2Form((f) => f ? { ...f, signedMdnRequired: e.target.checked } : f)} /><span>Signed MDN required</span></label>
              {as2Error ? <div className="form-error full-width">{as2Error}</div> : null}
              <div className="form-actions full-width">
                <DisabledActionButton reason={as2ProfileSaveDisabledReason(as2Form, as2Saving)} onClick={requestSaveAs2Profile}>
                  {as2Saving ? 'Saving...' : as2Form.id ? 'Update profile' : 'Create profile'}
                </DisabledActionButton>
                <button type="button" className="ghost-button" onClick={() => { setAs2Form(null); setAs2Error(null); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <table className="data-table">
              <thead><tr><th>Code</th><th>Name</th><th>Environment</th><th>MDN mode</th><th>Signing</th><th>Encryption</th><th /></tr></thead>
              <tbody>
                {as2Profiles.length === 0 ? <tr><td colSpan={7}>No AS2 profiles for this partner.</td></tr> : null}
                {as2Profiles.map((profile) => (
                  <tr key={profile.id ?? profile.code}>
                    <td>{profile.code}</td>
                    <td>{profile.profileName ?? profile.code}</td>
                    <td>{profile.environmentProfile}</td>
                    <td>{profile.mdnMode}</td>
                    <td>{profile.outboundSigningEnabled ? 'Yes' : 'No'}</td>
                    <td>{profile.outboundEncryptionEnabled ? 'Yes' : 'No'}</td>
                    <td><button type="button" onClick={() => setAs2Form({
                      id: profile.id,
                      environmentProfile: (profile.environmentProfile as 'DEV' | 'TEST' | 'UAT' | 'PROD') ?? 'DEV',
                      code: profile.code ?? '',
                      profileName: profile.profileName ?? '',
                      as2Identifier: profile.as2Identifier ?? '',
                      outboundEndpoint: profile.outboundEndpoint ?? '',
                      mdnMode: (profile.mdnMode as 'NONE' | 'SYNCHRONOUS' | 'ASYNCHRONOUS') ?? 'ASYNCHRONOUS',
                      outboundSigningEnabled: profile.outboundSigningEnabled ?? false,
                      outboundEncryptionEnabled: profile.outboundEncryptionEnabled ?? false,
                      signedMdnRequired: profile.signedMdnRequired ?? false,
                    })}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}

      {versionPartnerId ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Configuration history — {partners.find((partner) => partner.id === versionPartnerId)?.name}</h2>
              <p>Rollback creates a new audited version from the selected snapshot; approval state remains governed separately.</p>
            </div>
            <button type="button" className="ghost-button" onClick={() => setVersionPartnerId(null)}>Close</button>
          </div>
          <label className="full-width"><span>Rollback reason</span>
            <input value={rollbackReason} onChange={(event) => setRollbackReason(event.target.value)} placeholder="Required audit reason" />
          </label>
          {versionError ? <div className="form-error">{versionError}</div> : null}
          <table className="data-table">
            <thead><tr><th>Version</th><th>Lifecycle</th><th>Actor</th><th>Reason</th><th>Created</th><th /></tr></thead>
            <tbody>
              {versions.map((version) => (
                <tr key={version.version}>
                  <td>v{version.version}</td>
                  <td><StatusBadge value={version.lifecycleStatus} /></td>
                  <td>{version.actor}</td>
                  <td>{version.reason ?? '—'}</td>
                  <td>{new Date(version.createdAt).toLocaleString('en-GB')}</td>
                  <td>
                    <button type="button" disabled={versionBusy || !rollbackReason.trim()} onClick={() => void rollbackTo(version.version)}>
                      Restore as draft
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

const RUNTIME_FIELD_LABELS: Record<string, string> = {
  endpoint: 'Endpoint', authenticationType: 'Authentication', timeoutMs: 'Timeout (ms)',
  maxAttempts: 'Maximum attempts', backoffMs: 'Retry backoff (ms)',
};

function diffRuntimeProfiles(
  draft: PartnerRuntimeProfileRecord,
  active: PartnerRuntimeProfileRecord,
): Array<{ label: string; draftValue: string; activeValue: string }> {
  const diffs: Array<{ label: string; draftValue: string; activeValue: string }> = [];
  for (const draftDestination of draft.destinations) {
    const activeDestination = active.destinations.find((item) => item.code === draftDestination.code);
    (['endpoint', 'authenticationType', 'timeoutMs', 'maxAttempts', 'backoffMs'] as const).forEach((field) => {
      const draftValue = String(draftDestination[field] ?? '');
      const activeValue = String(activeDestination?.[field] ?? '(not active)');
      if (draftValue !== activeValue) {
        diffs.push({ label: `${draftDestination.code} — ${RUNTIME_FIELD_LABELS[field]}`, draftValue, activeValue });
      }
    });
    const draftSecretRefs = JSON.stringify(draftDestination.secretRefs.map((ref) => ref.reference));
    const activeSecretRefs = JSON.stringify((activeDestination?.secretRefs ?? []).map((ref) => ref.reference));
    if (draftSecretRefs !== activeSecretRefs) {
      diffs.push({ label: `${draftDestination.code} — Secret references`, draftValue: draftSecretRefs, activeValue: activeSecretRefs });
    }
  }
  for (const draftFamily of draft.messageFamilies) {
    const activeFamily = active.messageFamilies.find((item) => item.code === draftFamily.code);
    if (draftFamily.enabled !== (activeFamily?.enabled ?? false)) {
      diffs.push({
        label: `Message family — ${RUNTIME_MESSAGE_FAMILY_LABELS[draftFamily.code] ?? draftFamily.code}`,
        draftValue: draftFamily.enabled ? 'Enabled' : 'Disabled',
        activeValue: activeFamily?.enabled ? 'Enabled' : 'Disabled',
      });
    }
  }
  return diffs;
}

// Active-vs-draft visibility, connectivity diagnostics, and destination-allowlist status —
// closes the gaps flagged in docs/audits/integration-ui-operations-coverage/UI_OPERATIONS_COVERAGE_AUDIT.md
// §9 (active/draft), §10 (connectivity), §6 (allowlist).
function PartnerOperationsPanel({
  partner,
  destinationMetadata,
}: {
  partner: PartnerRecord;
  destinationMetadata: PartnerDestinationMetadata[];
}) {
  const [connectivity, setConnectivity] = useState<PartnerConnectivityValidationRecord[]>([]);
  const [policy, setPolicy] = useState<DestinationPolicyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testingCode, setTestingCode] = useState<string | null>(null);
  const canTest = usePermission('integration.partner.connectivity.test');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      configApi.partners.connectivity(partner.id),
      configApi.partners.destinationPolicy(partner.id),
    ]).then(([connectivityResult, policyResult]) => {
      if (cancelled) return;
      setConnectivity(connectivityResult);
      setPolicy(policyResult);
    }).catch((caught: unknown) => {
      if (!cancelled) setError(caught instanceof Error ? caught.message : 'Unable to load partner diagnostics.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [partner.id]);

  async function runTest(destination: PartnerRuntimeDestinationRecord) {
    setTestingCode(destination.code);
    setError(null);
    try {
      const protocol = destination.code === 'ADII_AS2' ? 'AS2_GATEWAY' as const : 'REST' as const;
      const result = await configApi.partners.testConnectivity(partner.id, { protocol, endpoint: destination.endpoint || undefined });
      setConnectivity((current) => [result, ...current]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Connectivity test failed.');
    } finally {
      setTestingCode(null);
    }
  }

  const draft = normalizedRuntimeProfile(partner.runtimeProfile);
  const active = partner.activeRuntimeProfile;
  const diffs = active ? diffRuntimeProfiles(draft, normalizedRuntimeProfile(active)) : [];
  const activeState = !active ? 'NOT_YET_ACTIVE' : diffs.length === 0 ? 'SYNCHRONIZED' : 'DRAFT_DIFFERS';

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>Runtime status — {partner.name}</h2>
          <p>Active-vs-draft configuration, connectivity diagnostics, and destination allowlist status.</p>
        </div>
      </div>

      <div className="summary-grid">
        <div>
          <strong>Active configuration</strong>
          <span>
            {activeState === 'NOT_YET_ACTIVE' ? 'Not yet active — this partner has never been approved.' : null}
            {activeState === 'SYNCHRONIZED' ? 'Active and synchronized with the current draft.' : null}
            {activeState === 'DRAFT_DIFFERS' ? `Draft differs from active in ${diffs.length} field(s) — approve to promote the draft.` : null}
          </span>
        </div>
      </div>

      {activeState === 'DRAFT_DIFFERS' ? (
        <table className="data-table">
          <thead><tr><th>Field</th><th>Draft (unsaved changes)</th><th>Active (live)</th></tr></thead>
          <tbody>
            {diffs.map((diff) => (
              <tr key={diff.label}>
                <td>{diff.label}</td>
                <td className="mono">{diff.draftValue}</td>
                <td className="mono">{diff.activeValue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <h3>Destination allowlist status</h3>
      {loading ? <LoadingState label="Loading diagnostics..." /> : null}
      {error ? <ErrorState message={error} /> : null}
      {!loading ? (
        <table className="data-table">
          <thead><tr><th>Destination</th><th>Allowlist status</th><th>Connectivity</th><th /></tr></thead>
          <tbody>
            {draft.destinations.filter((destination) => destination.endpoint.trim()).map((destination) => {
              const destMeta = destinationMetadata.find((item) => item.code === destination.code);
              const policyResult = policy.find((item) => item.code === destination.code);
              const latestResult = connectivity.find((item) =>
                item.protocol === (destination.code === 'ADII_AS2' ? 'AS2_GATEWAY' : 'REST'));
              const isLiveProbe = latestResult ? (latestResult.responseMetadata as { mode?: string })?.mode !== 'configuration-validation' : true;
              return (
                <tr key={destination.code}>
                  <td>{destMeta?.label ?? destination.code}</td>
                  <td>
                    {policyResult ? <StatusBadge value={policyResult.status} /> : <span>—</span>}
                    {policyResult?.status !== 'POLICY_NOT_APPLICABLE' && policyResult?.status !== 'ALLOWED' ? (
                      <div className="field-hint">{policyResult?.explanation}</div>
                    ) : null}
                  </td>
                  <td>
                    {latestResult ? (
                      <>
                        <StatusBadge value={latestResult.status} />
                        <div className="field-hint">
                          {isLiveProbe
                            ? `Live probe · ${new Date(latestResult.checkedAt).toLocaleString('en-GB')}`
                            : 'Configuration validated only — not a live connection test.'}
                        </div>
                      </>
                    ) : <span>Not tested yet</span>}
                  </td>
                  <td>
                    {canTest ? (
                      <button
                        type="button"
                        disabled={testingCode === destination.code || (destMeta ? !destMeta.selectable : false)}
                        onClick={() => void runTest(destination)}
                      >
                        {testingCode === destination.code ? 'Testing...' : 'Test connectivity'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {draft.destinations.filter((destination) => destination.endpoint.trim()).length === 0 ? (
              <tr><td colSpan={4}>No destinations configured yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

// PartnerLifecycleActions: per-action permission gating (matching ExchangeDetailPage/As2DlqPage)
// and a mandatory operator reason on every transition, via ConfirmDialog.
function PartnerLifecycleActions({
  partner,
  onChanged,
  onManageAs2,
}: {
  partner: PartnerRecord;
  onChanged: () => Promise<void>;
  onManageAs2: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  const canSubmit = usePermission('integration.partner.submit');
  const canApprove = usePermission('integration.partner.approve');
  const canReject = usePermission('integration.partner.reject');
  const canSuspend = usePermission('integration.partner.suspend');
  const canReactivate = usePermission('integration.partner.reactivate');

  async function run(caller: (reasonText: string) => Promise<PartnerRecord>, reasonText: string) {
    setBusy(true);
    setError(null);
    try {
      await caller(reasonText);
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Partner lifecycle action failed.');
      throw caught;
    } finally {
      setBusy(false);
    }
  }

  function buildTransitionConfirmState(
    action: 'submit' | 'approve' | 'reject' | 'suspend' | 'reactivate',
    label: string,
    message: string,
    caller: (reasonText: string) => Promise<PartnerRecord>,
    reasonText: string,
  ): ConfirmDialogState {
    return {
      title: `${label} — ${partner.name}`,
      message,
      confirmLabel: label,
      risk: action === 'reject' || action === 'suspend' ? 'high' : 'medium',
      detailLabel: 'Reason (required)',
      detailValue: reasonText,
      detailPlaceholder: `Why are you ${label.toLowerCase()}ing this partner?`,
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildTransitionConfirmState(action, label, message, caller, value)),
      onConfirm: () => run(caller, reasonText),
    };
  }

  function openTransition(
    action: 'submit' | 'approve' | 'reject' | 'suspend' | 'reactivate',
    label: string,
    message: string,
    caller: (reasonText: string) => Promise<PartnerRecord>,
  ) {
    setConfirm(buildTransitionConfirmState(action, label, message, caller, ''));
  }

  const status = partner.lifecycleStatus ?? 'DRAFT';
  return (
    <>
      <button type="button" disabled={busy} onClick={onManageAs2}>AS2 profiles</button>
      {status === 'DRAFT' && canSubmit ? (
        <button type="button" disabled={busy} onClick={() => openTransition(
          'submit', 'Submit', 'Submit this partner configuration for approval.',
          (reasonText) => configApi.partners.submit(partner.id, reasonText, partner.version),
        )}>Submit</button>
      ) : null}
      {status === 'PENDING_APPROVAL' && canApprove ? (
        <button type="button" disabled={busy} onClick={() => openTransition(
          'approve', 'Approve', 'Approve this partner configuration and promote it to the active runtime profile.',
          (reasonText) => configApi.partners.approve(partner.id, reasonText, partner.version),
        )}>Approve</button>
      ) : null}
      {status === 'PENDING_APPROVAL' && canReject ? (
        <button type="button" disabled={busy} onClick={() => openTransition(
          'reject', 'Reject', 'Reject this partner configuration and return it to draft.',
          (reasonText) => configApi.partners.reject(partner.id, reasonText, partner.version),
        )}>Reject</button>
      ) : null}
      {status === 'ACTIVE' && canSuspend ? (
        <button type="button" disabled={busy} onClick={() => openTransition(
          'suspend', 'Suspend', 'Suspend this partner. Runtime traffic for this partner will be blocked.',
          (reasonText) => configApi.partners.suspend(partner.id, reasonText, partner.version),
        )}>Suspend</button>
      ) : null}
      {status === 'SUSPENDED' && canReactivate ? (
        <button type="button" disabled={busy} onClick={() => openTransition(
          'reactivate', 'Reactivate', 'Reactivate this partner and resume runtime traffic.',
          (reasonText) => configApi.partners.reactivate(partner.id, reasonText, partner.version),
        )}>Reactivate</button>
      ) : null}
      {error ? <span className="field-error">{error}</span> : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}

// ─── Inbound policies page (Fix 6: partner dropdown) ─────────────────────────

function defaultInboundPolicy(): InboundPolicyRecord {
  return {
    id: '', name: '', partnerId: '', tenantId: '', flowKey: '',
    protocol: 'WEBHOOK', authType: 'HMAC', secretRef: '', signatureKeyId: '',
    algorithm: 'HMAC_SHA256', allowedIps: [],
    allowedClockSkewSeconds: 300, replayWindowSeconds: 300, isActive: true,
    createdAt: '', updatedAt: '',
  };
}

export function InboundPoliciesPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<InboundPolicyRecord>(defaultInboundPolicy);
  const [allowedIpsJson, setAllowedIpsJson] = useState('[]');
  const [allowedIpsError, setAllowedIpsError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  const { data, isLoading, error, refresh } = useAsyncData(() => configApi.inboundPolicies.list({ page: 1, pageSize: 100 }), []);
  const { data: partnerData } = useAsyncData(() => configApi.partners.list({ page: 1, pageSize: 100, isActive: true }), []);
  const policies = data?.data ?? [];
  const partners = partnerData?.data ?? [];

  function editPolicy(policy: InboundPolicyRecord) {
    setSelectedId(policy.id);
    setForm(policy);
    setAllowedIpsJson(JSON.stringify(policy.allowedIps ?? [], null, 2));
    setAllowedIpsError(null);
    setSubmitError(null);
  }

  function resetForm() {
    setSelectedId(null);
    setForm(defaultInboundPolicy());
    setAllowedIpsJson('[]');
    setAllowedIpsError(null);
    setSubmitError(null);
  }

  function patch(updates: Partial<InboundPolicyRecord>) {
    setForm((current) => ({ ...current, ...updates }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.partnerId) { setSubmitError('Partner is required.'); return; }
    let allowedIps: string[];
    try {
      allowedIps = JSON.parse(allowedIpsJson || '[]') as string[];
    } catch {
      setAllowedIpsError('Allowed IPs must be a valid JSON string array.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = toInboundPolicyPayload({ ...form, allowedIps });
      if (selectedId) await configApi.inboundPolicies.update(selectedId, payload);
      else await configApi.inboundPolicies.create(payload);
      resetForm();
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to save inbound policy.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="page-grid two-columns">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Inbound webhook policies</h2>
            <p>Manage partner authentication, replay protection, source allowlists, and flow-specific webhook policies.</p>
          </div>
        </div>
        {isLoading ? <LoadingState /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && policies.length === 0 ? <EmptyState label="No inbound policies configured." /> : null}
        {policies.length ? (
          <table className="data-table">
            <thead><tr><th>Partner</th><th>Flow key</th><th>Auth</th><th>Protocol</th><th>Active</th><th /></tr></thead>
            <tbody>{policies.map((policy) => {
              const partner = partners.find((p) => p.id === policy.partnerId);
              return (
                <tr key={policy.id}>
                  <td>{partner ? partner.name : policy.partnerId}</td>
                  <td>{policy.flowKey ?? '—'}</td>
                  <td>{policy.authType}</td>
                  <td>{policy.protocol}</td>
                  <td><StatusBadge value={policy.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                  <td className="actions-cell">
                    <button type="button" onClick={() => editPolicy(policy)}>Edit</button>
                    <button type="button" className="danger-link" onClick={() => setConfirm({
                      title: 'Delete inbound policy',
                      message: `Delete the inbound policy for ${partner?.name ?? policy.partnerId}.`,
                      confirmLabel: 'Delete policy', risk: 'high', requireText: 'DELETE',
                      onConfirm: async () => { await configApi.inboundPolicies.remove(policy.id); await refresh(); },
                    })}>Delete</button>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>{selectedId ? 'Edit policy' : 'Create policy'}</h2>
          {selectedId ? <button type="button" className="ghost-button" onClick={resetForm}>New</button> : null}
        </div>
        <form className="form-grid" onSubmit={submit}>
          {/* Fix 6: partner dropdown replaces the plain text "Partner ID" field */}
          <label className="full-width">
            <span>Partner</span>
            <select required value={form.partnerId} onChange={(e) => patch({ partnerId: e.target.value })}>
              <option value="">Select partner</option>
              {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>)}
            </select>
          </label>
          <label><span>Tenant ID</span><input value={form.tenantId ?? ''} onChange={(e) => patch({ tenantId: e.target.value })} /></label>
          <label><span>Flow key</span><input value={form.flowKey ?? ''} placeholder="e.g. CUSCAR_INBOUND" onChange={(e) => patch({ flowKey: e.target.value })} /></label>
          <label><span>Protocol</span>
            <select value={form.protocol} onChange={(e) => patch({ protocol: e.target.value as InboundPolicyRecord['protocol'] })}>
              {['WEBHOOK', 'REST', 'KAFKA', 'SFTP', 'XML', 'SOAP'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label><span>Authentication</span>
            <select value={form.authType} onChange={(e) => patch({ authType: e.target.value as 'HMAC' | 'NONE' })}>
              <option value="HMAC">HMAC</option><option value="NONE">None</option>
            </select>
          </label>
          <label><span>Secret reference</span><input value={form.secretRef ?? ''} placeholder="ENV:WEBHOOK_SECRET" onChange={(e) => patch({ secretRef: e.target.value })} /></label>
          <label><span>Signature key ID</span><input value={form.signatureKeyId ?? ''} onChange={(e) => patch({ signatureKeyId: e.target.value })} /></label>
          <label><span>Algorithm</span>
            <select value={form.algorithm} onChange={(e) => patch({ algorithm: e.target.value as 'HMAC_SHA256' | 'HMAC_SHA512' })}>
              <option value="HMAC_SHA256">HMAC-SHA256</option>
              <option value="HMAC_SHA512">HMAC-SHA512</option>
            </select>
          </label>
          <label><span>Clock skew tolerance (s)</span><input type="number" min={0} value={form.allowedClockSkewSeconds} onChange={(e) => patch({ allowedClockSkewSeconds: Number(e.target.value) })} /></label>
          <label><span>Replay window (s)</span><input type="number" min={0} value={form.replayWindowSeconds} onChange={(e) => patch({ replayWindowSeconds: Number(e.target.value) })} /></label>
          <label className="checkbox-field full-width"><input type="checkbox" checked={form.isActive} onChange={(e) => patch({ isActive: e.target.checked })} /><span>Policy is active</span></label>
          <label className="full-width">
            <span>Allowed source IPs <small>(JSON string array, empty = allow all)</small></span>
            <textarea rows={3} value={allowedIpsJson} onChange={(e) => setAllowedIpsJson(e.target.value)} />
            {allowedIpsError ? <span className="field-error">{allowedIpsError}</span> : null}
          </label>
          {submitError ? <div className="form-error full-width">{submitError}</div> : null}
          <div className="form-actions full-width">
            <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : selectedId ? 'Update policy' : 'Create policy'}</button>
          </div>
        </form>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

// ─── Unchanged versioned catalog pages ───────────────────────────────────────

export function MappingsPage() {
  return (
    <VersionedCatalogPage<MappingRecord, MappingVersionRecord>
      title="Mappings"
      definitionKind="mapping"
      editPermission="flow.operations.execute"
      approvePermission="flow.analytics.read"
      activatePermission="flow.operations.execute"
      list={(query) => mappingsApi.list(query)}
      create={(payload) => mappingsApi.createMapping(payload as Omit<MappingRecord, 'id' | 'createdAt' | 'updatedAt'>)}
      remove={mappingsApi.deleteMapping}
      listVersions={mappingsApi.listMappingVersions}
      createVersion={(id, value, changeLog) => mappingsApi.createMappingVersion(id, { ruleDefinition: value, changeLog })}
      transition={mappingsApi.updateMappingVersionLifecycle}
      readEditorValue={(version) => version.ruleDefinition}
      defaultCreateValue={{
        id: '', name: '', documentType: 'CUSCAR', version: 'v1', versionNumber: 1,
        definitionId: '', selectedVersionId: '', currentVersionId: '', lifecycleState: 'DRAFT',
        isActive: true, sourceSchemaRef: '', targetFormat: 'EDI', ruleDefinition: {},
        createdAt: '', updatedAt: '',
      }}
      buildCreatePayload={(draft, value) => ({ ...draft, ruleDefinition: value })}
    />
  );
}

export function TemplatesPage() {
  return (
    <VersionedCatalogPage<TemplateRecord, TemplateVersionRecord>
      title="Templates"
      definitionKind="template"
      editPermission="flow.operations.execute"
      approvePermission="flow.analytics.read"
      activatePermission="flow.operations.execute"
      list={(query) => templatesApi.list(query)}
      create={(payload) => templatesApi.createTemplate(payload as Omit<TemplateRecord, 'id' | 'createdAt' | 'updatedAt'>)}
      remove={templatesApi.deleteTemplate}
      listVersions={templatesApi.listTemplateVersions}
      createVersion={(id, value, changeLog) => templatesApi.createTemplateVersion(id, { templateBody: String(value.templateBody ?? ''), defaultSourcePayload: value.defaultSourcePayload as Record<string, unknown>, description: String(value.description ?? ''), changeLog })}
      transition={templatesApi.updateTemplateVersionLifecycle}
      readEditorValue={(version) => ({ description: version.description ?? '', templateBody: version.templateBody ?? '', defaultSourcePayload: version.defaultSourcePayload ?? {} })}
      defaultCreateValue={{
        id: '', name: '', documentType: 'CUSCAR', description: '', partnerConfigId: '',
        templateBody: '', defaultSourcePayload: {}, isActive: true, createdAt: '', updatedAt: '',
      }}
      buildCreatePayload={(draft, value) => ({ ...draft, description: String(value.description ?? ''), templateBody: String(value.templateBody ?? ''), defaultSourcePayload: value.defaultSourcePayload as Record<string, unknown> })}
    />
  );
}

export function ValidationRulesPage() {
  return (
    <VersionedCatalogPage<ValidationRuleRecord, ValidationRuleVersionRecord>
      title="Validation Rules"
      definitionKind="validation rule"
      editPermission="flow.operations.execute"
      approvePermission="flow.analytics.read"
      activatePermission="flow.operations.execute"
      list={(query) => validationRulesApi.list(query)}
      create={(payload) => validationRulesApi.createValidationRule(payload as Omit<ValidationRuleRecord, 'id' | 'createdAt' | 'updatedAt'>)}
      remove={validationRulesApi.deleteValidationRule}
      listVersions={validationRulesApi.listValidationRuleVersions}
      createVersion={(id, value, changeLog) => validationRulesApi.createValidationRuleVersion(id, { ruleDefinition: value.ruleDefinition as Record<string, unknown>, severity: value.severity as 'error' | 'warning', ruleType: value.ruleType as 'structure' | 'semantic', changeLog })}
      transition={validationRulesApi.updateValidationRuleVersionLifecycle}
      readEditorValue={(version) => ({ severity: version.severity, ruleType: version.ruleType, ruleDefinition: version.ruleDefinition })}
      defaultCreateValue={{
        id: '', name: '', documentType: 'CUSCAR', severity: 'error', ruleType: 'semantic',
        ruleDefinition: {}, isActive: true, createdAt: '', updatedAt: '',
      }}
      buildCreatePayload={(draft, value) => ({ ...draft, severity: value.severity as 'error' | 'warning', ruleType: value.ruleType as 'structure' | 'semantic', ruleDefinition: value.ruleDefinition as Record<string, unknown> })}
    />
  );
}

export function ConnectorDefinitionsPage() {
  return (
    <ConfigResourcePage<ConnectorDefinitionRecord>
      title="Connector catalog"
      description="Register production transport drivers for REST, webhook, SFTP, and Kafka delivery."
      defaultValue={{
        id: '', name: '', code: '', protocolFamily: 'REST', capabilityType: 'OUTBOUND_DELIVERY',
        description: '', driverRef: 'connector.rest.v1',
        supportedAuthSchemes: ['API_KEY', 'OAUTH2_CLIENT_CREDENTIALS'], isActive: true, createdAt: '', updatedAt: '',
      }}
      fields={[
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'code', label: 'Code', type: 'text' },
        { key: 'protocolFamily', label: 'Protocol', type: 'select', options: protocolOptions },
        { key: 'capabilityType', label: 'Capability', type: 'select', options: [{ label: 'OUTBOUND_DELIVERY', value: 'OUTBOUND_DELIVERY' }] },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'driverRef', label: 'Driver ref', type: 'text' },
        { key: 'supportedAuthSchemes', label: 'Supported auth schemes JSON', type: 'json' },
        { key: 'isActive', label: 'Active', type: 'checkbox' },
      ]}
      list={configApi.connectorDefinitions.list}
      create={(payload) => configApi.connectorDefinitions.create(toConnectorDefinitionCreatePayload(payload))}
      update={(id, payload) => configApi.connectorDefinitions.update(id, toConnectorDefinitionUpdatePayload(payload))}
      remove={configApi.connectorDefinitions.remove}
    />
  );
}

export function ConnectorInstancesPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<ConnectorInstanceRecord>(() => createDefaultConnectorInstance());
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>(() => createConnectorJsonDrafts(createDefaultConnectorInstance()));
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(() => configApi.connectorInstances.list({ page: 1, pageSize: 100 }), []);
  const { data: partnerData } = useAsyncData(() => configApi.partners.list({ page: 1, pageSize: 100 }), []);
  const { data: definitionData } = useAsyncData(() => configApi.connectorDefinitions.list({ page: 1, pageSize: 100, isActive: true }), []);
  const instances = data?.data ?? [];
  const partners = partnerData?.data ?? [];
  const definitions = definitionData?.data ?? [];

  function edit(item: ConnectorInstanceRecord) {
    setSelectedId(item.id);
    setForm(item);
    setJsonDrafts(createConnectorJsonDrafts(item));
    setJsonErrors({});
    setSubmitError(null);
  }

  function patch(updates: Partial<ConnectorInstanceRecord>) {
    setForm((current) => ({ ...current, ...updates }));
  }

  function resetConnectorInstanceForm() {
    const next = createDefaultConnectorInstance();
    setSelectedId(null);
    setForm(next);
    setJsonDrafts(createConnectorJsonDrafts(next));
    setJsonErrors({});
    setSubmitError(null);
  }

  function parseAdvancedJsonDraft(key: 'health' | 'config' | 'secretRefs'): unknown {
    try {
      const fallback = key === 'secretRefs' ? '[]' : '{}';
      const parsed = JSON.parse((jsonDrafts[key] ?? fallback).trim() || fallback);
      setJsonErrors((current) => { const next = { ...current }; delete next[key]; return next; });
      if (key === 'health') patch({ health: parsed as ConnectorInstanceRecord['health'] });
      if (key === 'config') patch({ config: parsed as Record<string, unknown> });
      if (key === 'secretRefs') patch({ secretRefs: parsed as ConnectorInstanceRecord['secretRefs'] });
      return parsed;
    } catch {
      const label = key === 'secretRefs' ? 'Secret references JSON' : key === 'health' ? 'Health JSON' : 'Config JSON';
      setJsonErrors((current) => ({ ...current, [key]: `${label} must contain valid JSON.` }));
      return undefined;
    }
  }

  function buildConnectorPayload(): ConnectorInstanceRecord | null {
    const errors: Record<string, string> = {};
    const next = { ...form };
    const fields: Array<'health' | 'config' | 'secretRefs'> = ['health', 'config', 'secretRefs'];
    for (const key of fields) {
      try {
        const fallback = key === 'secretRefs' ? '[]' : '{}';
        const parsed = JSON.parse((jsonDrafts[key] ?? fallback).trim() || fallback);
        if (key === 'health') next.health = parsed as ConnectorInstanceRecord['health'];
        if (key === 'config') next.config = parsed as Record<string, unknown>;
        if (key === 'secretRefs') next.secretRefs = parsed as ConnectorInstanceRecord['secretRefs'];
      } catch {
        const label = key === 'secretRefs' ? 'Secret references JSON' : key === 'health' ? 'Health JSON' : 'Config JSON';
        errors[key] = `${label} must contain valid JSON.`;
      }
    }
    setJsonErrors(errors);
    if (Object.keys(errors).length > 0) { setSubmitError('Fix invalid JSON before saving.'); return null; }
    setForm(next);
    return next;
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const nextForm = buildConnectorPayload();
      if (!nextForm) return;
      const payload = toConnectorInstanceCreatePayload(nextForm);
      if (selectedId) await configApi.connectorInstances.update(selectedId, payload);
      else await configApi.connectorInstances.create(payload);
      resetConnectorInstanceForm();
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Unable to save connector instance.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="page-grid two-columns">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Connector Instances</h2>
            <p>Configure partner transport instances with concrete endpoint, timeout, retry, and ownership settings.</p>
          </div>
        </div>
        {isLoading ? <LoadingState /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && instances.length === 0 ? <EmptyState label="No connector instances configured." /> : null}
        {instances.length ? (
          <table className="data-table">
            <thead><tr><th>Name</th><th>Protocol</th><th>Endpoint</th><th>Health</th><th /></tr></thead>
            <tbody>{instances.map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.protocolFamily}</td>
                <td>{item.endpoint ?? 'n/a'}</td>
                <td><StatusBadge value={item.health.status} /></td>
                <td className="actions-cell">
                  <button type="button" onClick={() => edit(item)}>Edit</button>
                  <button type="button" className="danger-link" onClick={() => setConfirm({ title: 'Delete connector instance', message: `Delete ${item.name}.`, confirmLabel: 'Delete instance', risk: 'high', requireText: 'DELETE', onConfirm: async () => { await configApi.connectorInstances.remove(item.id); await refresh(); } })}>Delete</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>{selectedId ? 'Edit connector instance' : 'Create connector instance'}</h2>
          {selectedId ? <button type="button" className="ghost-button" onClick={resetConnectorInstanceForm}>New</button> : null}
        </div>
        <form className="form-grid" onSubmit={submit}>
          <label><span>Name</span><input value={form.name} onChange={(event) => patch({ name: event.target.value })} /></label>
          <label><span>Partner</span><select value={form.partnerConfigId ?? ''} onChange={(event) => patch({ partnerConfigId: event.target.value })}><option value="">Unassigned</option>{partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></label>
          <label><span>Connector definition</span><select value={form.definitionId} onChange={(event) => {
            const definition = definitions.find((item) => item.id === event.target.value);
            patch({ definitionId: event.target.value, protocolFamily: definition?.protocolFamily ?? form.protocolFamily });
          }}><option value="">Select definition</option>{definitions.map((definition) => <option key={definition.id} value={definition.id}>{definition.name} / {definition.protocolFamily}</option>)}</select></label>
          <label><span>Protocol family</span><select value={form.protocolFamily} onChange={(event) => patch({ protocolFamily: event.target.value as ConnectorInstanceRecord['protocolFamily'] })}>{protocolOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label className="full-width"><span>Endpoint</span><input value={form.endpoint ?? ''} onChange={(event) => patch({ endpoint: event.target.value })} /></label>
          <label><span>Timeout ms</span><input type="number" value={Number(form.timeoutPolicy.requestTimeoutMs ?? 0)} onChange={(event) => patch({ timeoutPolicy: { ...form.timeoutPolicy, requestTimeoutMs: Number(event.target.value) } })} /></label>
          <label><span>Max retry attempts</span><input type="number" value={Number(form.retryPolicy.maxAttempts ?? 0)} onChange={(event) => patch({ retryPolicy: { ...form.retryPolicy, maxAttempts: Number(event.target.value) } })} /></label>
          <label className="checkbox-field"><input type="checkbox" checked={form.isActive} onChange={(event) => patch({ isActive: event.target.checked })} /><span>Active</span></label>
          <div className="full-width panel-actions"><button type="button" className="ghost-button" onClick={() => setAdvancedOpen((open) => !open)}>Advanced JSON</button></div>
          {advancedOpen ? (
            <>
              <label className="full-width"><span>Health JSON</span><textarea rows={5} value={jsonDrafts.health} onChange={(event) => setJsonDrafts((current) => ({ ...current, health: event.target.value }))} onBlur={() => parseAdvancedJsonDraft('health')} />{jsonErrors.health ? <span className="field-error">{jsonErrors.health}</span> : null}</label>
              <label className="full-width"><span>Config JSON</span><textarea rows={6} value={jsonDrafts.config} onChange={(event) => setJsonDrafts((current) => ({ ...current, config: event.target.value }))} onBlur={() => parseAdvancedJsonDraft('config')} />{jsonErrors.config ? <span className="field-error">{jsonErrors.config}</span> : null}</label>
              <label className="full-width"><span>Secret references JSON</span><textarea rows={4} value={jsonDrafts.secretRefs} onChange={(event) => setJsonDrafts((current) => ({ ...current, secretRefs: event.target.value }))} onBlur={() => parseAdvancedJsonDraft('secretRefs')} />{jsonErrors.secretRefs ? <span className="field-error">{jsonErrors.secretRefs}</span> : null}</label>
            </>
          ) : null}
          {submitError ? <div className="form-error full-width">{submitError}</div> : null}
          <div className="form-actions full-width"><button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save connector instance'}</button></div>
        </form>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

// ─── Versioned catalog (Fix 10/2: mapping designer link) ─────────────────────

type VersionedRecord = {
  id: string;
  name: string;
  documentType: DocumentType;
  updatedAt: string;
  version?: string;
  versionLabel?: string;
  activeVersionId?: string;
  currentVersionId?: string;
  selectedVersionId?: string;
  lifecycleState?: GovernanceLifecycleState;
  isActive: boolean;
};

type VersionRecord = {
  id: string;
  versionNumber: number;
  versionLabel: string;
  lifecycleState: GovernanceLifecycleState;
  createdAt: string;
  updatedAt: string;
};

function VersionedCatalogPage<T extends VersionedRecord, V extends VersionRecord>({
  title, definitionKind, editPermission, approvePermission, activatePermission,
  list, create, remove, listVersions, createVersion, transition,
  readEditorValue, defaultCreateValue, buildCreatePayload,
}: {
  title: string;
  definitionKind: string;
  editPermission: Permission;
  approvePermission: Permission;
  activatePermission: Permission;
  list: (query: Record<string, string | number | boolean | undefined>) => Promise<{ data: T[]; total: number }>;
  create: (payload: T) => Promise<T>;
  remove: (id: string) => Promise<void>;
  listVersions: (id: string) => Promise<V[]>;
  createVersion: (id: string, value: Record<string, unknown>, changeLog: string) => Promise<T>;
  transition: (id: string, versionId: string, action: 'validate' | 'approve' | 'activate' | 'reject') => Promise<T>;
  readEditorValue: (version: V) => Record<string, unknown>;
  defaultCreateValue: T;
  buildCreatePayload: (draft: T, value: Record<string, unknown>) => T;
}) {
  const { search } = useLocation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [versions, setVersions] = useState<V[]>([]);
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [draft, setDraft] = useState<T>(defaultCreateValue);
  const [editorValue, setEditorValue] = useState('{}');
  const [changeLog, setChangeLog] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [showRuleHelp, setShowRuleHelp] = useState(false);
  const [page, setPage] = useState(1);
  const canEdit = usePermission(editPermission);
  const canApprove = usePermission(approvePermission);
  const canActivate = usePermission(activatePermission);
  const { data, isLoading, error, refresh } = useAsyncData(() => list({ page, pageSize: VERSIONED_CATALOG_PAGE_SIZE }), [page]);
  const records = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / VERSIONED_CATALOG_PAGE_SIZE));
  const selected = records.find((item) => item.id === selectedId) ?? null;
  const selectedVersion = versions.find((item) => item.id === selectedVersionId) ?? versions[0] ?? null;
  const disabledHints = [
    !canEdit ? `Creating and editing ${definitionKind} versions requires ${editPermission}.` : '',
    !canApprove ? `Validation and approval require ${approvePermission}.` : '',
    !canActivate ? `Activation requires ${activatePermission}.` : '',
    selected && !selectedVersion ? 'Select a version before running lifecycle actions.' : '',
  ].filter(Boolean);

  async function selectRecord(record: T) {
    setSelectedId(record.id);
    setDraft(record);
    setSubmitError(null);
    const items = await listVersions(record.id);
    setVersions(items);
    const preferred = items.find((item) => item.lifecycleState === 'ACTIVE') ?? items[0] ?? null;
    setSelectedVersionId(preferred?.id ?? '');
    setEditorValue(JSON.stringify(preferred ? readEditorValue(preferred) : {}, null, 2));
    setChangeLog('');
    setTab('current');
  }

  function viewVersion(version: V) {
    setSelectedVersionId(version.id);
    setEditorValue(JSON.stringify(readEditorValue(version), null, 2));
    setTab('current');
  }

  async function createDefinition() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const value = JSON.parse(editorValue || '{}') as Record<string, unknown>;
      const created = await create(buildCreatePayload(draft, value));
      await refresh();
      await selectRecord(created);
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : `Unable to create ${definitionKind}.`);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function saveVersion() {
    if (!selected) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const value = JSON.parse(editorValue || '{}') as Record<string, unknown>;
      await createVersion(selected.id, value, changeLog);
      await selectRecord(selected);
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : `Unable to save ${definitionKind} version.`);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function transitionVersion(action: 'validate' | 'approve' | 'activate' | 'reject') {
    if (!selected || !selectedVersion) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await transition(selected.id, selectedVersion.id, action);
      await selectRecord(selected);
      await refresh();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : `Unable to update ${definitionKind} lifecycle.`);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="page-stack">
      {submitError ? <ErrorState message={submitError} /> : null}
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{title}</h2>
            <p>Governed definitions with active version status and lifecycle controls.</p>
          </div>
          <button type="button" onClick={() => { setSelectedId(null); setDraft(defaultCreateValue); setVersions([]); setEditorValue('{}'); setTab('current'); }}>Create new definition</button>
        </div>
        {isLoading ? <LoadingState /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && records.length === 0 ? <EmptyState label={`No ${title.toLowerCase()} found.`} /> : null}
        {records.length ? (
          <table className="data-table">
            <thead><tr><th>Definition Name</th><th>Active Version</th><th>Status</th><th>Last Updated</th><th>Actions</th></tr></thead>
            <tbody>{records.map((record) => (
              <tr key={record.id}>
                <td>{record.name}</td>
                <td>{record.version ?? record.versionLabel ?? record.activeVersionId ?? 'n/a'}</td>
                <td><StatusBadge value={record.lifecycleState ?? (record.isActive ? 'ACTIVE' : 'DRAFT')} /></td>
                <td>{new Date(record.updatedAt).toLocaleString('en-GB')}</td>
                <td className="actions-cell">
                  <button type="button" onClick={() => void selectRecord(record)}>Edit</button>
                  <button type="button" className="danger-link" onClick={() => setConfirm({ title: `Delete ${definitionKind}`, message: `Delete ${record.name} and its governed versions.`, confirmLabel: 'Delete definition', risk: 'high', requireText: 'DELETE', onConfirm: async () => { await remove(record.id); await refresh(); } })}>Delete</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        ) : null}
        {total > VERSIONED_CATALOG_PAGE_SIZE ? (
          <div className="pagination-bar">
            <button type="button" className="ghost-button" disabled={page <= 1 || isLoading} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page} of {totalPages} &mdash; {total} total</span>
            <button type="button" className="ghost-button" disabled={page >= totalPages || isLoading} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{selected ? selected.name : `New ${definitionKind}`}</h2>
            <p>{selected ? 'Edit the selected version or inspect version history.' : 'Create a definition with its initial governed version.'}</p>
          </div>
          <div className="panel-actions">
            {selectedVersion ? <StatusBadge value={selectedVersion.lifecycleState} /> : null}
            {/* Fix 10/2: direct link to the visual mapping designer when a mapping is selected */}
            {definitionKind === 'mapping' && selected ? (
              <Link className="button-link ghost-button" to={{ pathname: '/mapping-designer', search }}>Open in Designer</Link>
            ) : null}
          </div>
        </div>
        <div className="tabs">
          <button type="button" aria-pressed={tab === 'current'} className={tab === 'current' ? 'active' : ''} onClick={() => setTab('current')}>Current version</button>
          <button type="button" aria-pressed={tab === 'history'} className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>Version history</button>
        </div>
        {disabledHints.length ? (
          <div className="permission-hints" role="note">
            {disabledHints.map((hint) => <span key={hint}>{hint}</span>)}
          </div>
        ) : null}
        {tab === 'current' ? (
          <>
            <div className="form-grid">
              <label><span>Name</span><input value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} /></label>
              <label><span>Document type</span><select value={draft.documentType} onChange={(event) => setDraft((current) => ({ ...current, documentType: event.target.value as DocumentType }))}>{documentTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
              {selected ? <label><span>Version</span><select value={selectedVersionId} onChange={(event) => viewVersion(versions.find((item) => item.id === event.target.value) ?? versions[0])}>{versions.map((version) => <option key={version.id} value={version.id}>{version.versionLabel} / {version.lifecycleState}</option>)}</select></label> : null}
              <label><span>Change log</span><input value={changeLog} onChange={(event) => setChangeLog(event.target.value)} /></label>
              <label className="full-width">
                <span className="label-with-help">
                  Version content JSON
                  {definitionKind === 'mapping' ? (
                    <button
                      type="button"
                      className="help-icon"
                      aria-label="Rule definition format reference"
                      title="Show format reference"
                      onClick={() => setShowRuleHelp((v) => !v)}
                    >?</button>
                  ) : null}
                </span>
                <textarea rows={16} value={editorValue} readOnly={!canEdit} onChange={(event) => setEditorValue(event.target.value)} />
              </label>
              {showRuleHelp && definitionKind === 'mapping' ? (
                <div className="rule-help-callout full-width">
                  <h3>Rule definition formats</h3>
                  <p>Three formats are supported. Pick whichever suits the complexity of the mapping.</p>

                  <h4>Format 1 — <code>fields</code> + <code>assign</code> &nbsp;<em>simplest, recommended for hand-written rules</em></h4>
                  <p><code>assign</code> writes hardcoded values. <code>fields</code> maps <em>source dot-path → target dot-path</em>. Both run together.</p>
                  <pre>{`{
  "assign": {
    "canonical.messageType": "CUSCAR",
    "canonical.source":      "OSS Integration"
  },
  "fields": {
    "canonical.vessel.imo":                "vessel.imo",
    "canonical.vessel.name":               "vessel.name",
    "canonical.cargoManifest.reference":   "manifestNo",
    "canonical.cargoManifest.portOfLoad":  "portOfLoading"
  }
}`}</pre>

                  <h4>Format 2 — <code>fieldMappings</code> array &nbsp;<em>supports per-field defaults and required guards</em></h4>
                  <p>Omit <code>sourcePath</code> to write <code>defaultValue</code> directly. Use <code>targetField</code> for document generation mappings; use <code>targetPath</code> for transformer/designer mappings.</p>
                  <pre>{`{
  "fieldMappings": [
    { "sourcePath": "shipment.bookingNumber",   "targetField": "bookingNumber",       "required": true },
    { "sourcePath": "shipment.voyageNumber",    "targetField": "voyageNumber",        "required": true },
    { "sourcePath": "equipment.isoCode",        "targetField": "equipmentIsoCode",    "defaultValue": "22G1" },
    { "sourcePath": "declarationSommaire",      "targetField": "declarationSommaire" }
  ]
}`}</pre>

                  <h4>Format 3 — Visual designer format &nbsp;<em>produced automatically when saving from the Mapping Designer page</em></h4>
                  <p>Contains executable <code>fieldMappings</code> and a <code>designerRules</code> copy that the Mapping Designer reloads when you re-open this version.</p>
                  <pre>{`{
  "fieldMappings": [
    { "sourcePath": "vessel.imo",  "targetPath": "canonical.vessel.imo" },
    {                              "targetPath": "canonical.messageType", "defaultValue": "CUSCAR" }
  ],
  "designerRules": [
    { "id": "rule-imo",  "type": "direct",   "sourcePath": "vessel.imo", "targetPath": "canonical.vessel.imo",  "expression": "source.vessel.imo" },
    { "id": "rule-type", "type": "constant", "sourcePath": "",           "targetPath": "canonical.messageType", "expression": "\\"CUSCAR\\"" }
  ],
  "updatedFrom": "mapping-designer"
}`}</pre>

                  <p style={{ marginTop: 12, color: '#496174' }}>
                    <strong>Rule types in designerRules:</strong> <code>direct</code> copies a source path · <code>constant</code> writes a fixed value (quote-wrap strings in expression) · <code>array</code> maps array element paths
                  </p>
                </div>
              ) : null}
            </div>
            <div className="panel-actions">
              {selected ? (
                <>
                  <button type="button" disabled={isSubmitting || !canEdit} title={!canEdit ? `Requires ${editPermission}` : undefined} onClick={() => void saveVersion()}>Save as new version</button>
                  <button type="button" disabled={isSubmitting || !canApprove || !selectedVersion} title={!selectedVersion ? 'Select a version first' : !canApprove ? `Requires ${approvePermission}` : undefined} onClick={() => void transitionVersion('validate')}>Validate</button>
                  <button type="button" disabled={isSubmitting || !canApprove || !selectedVersion} title={!selectedVersion ? 'Select a version first' : !canApprove ? `Requires ${approvePermission}` : undefined} onClick={() => void transitionVersion('approve')}>Approve</button>
                  <button type="button" disabled={isSubmitting || !canActivate || !selectedVersion} title={!selectedVersion ? 'Select a version first' : !canActivate ? `Requires ${activatePermission}` : undefined} onClick={() => void transitionVersion('activate')}>Activate</button>
                  {selectedVersion && (selectedVersion.lifecycleState === 'VALIDATED' || selectedVersion.lifecycleState === 'APPROVED') ? (
                    <button
                      type="button"
                      className="danger-link"
                      disabled={isSubmitting || !canApprove}
                      title={!canApprove ? `Requires ${approvePermission}` : undefined}
                      onClick={() => setConfirm({
                        title: `Reject ${definitionKind} version`,
                        message: `Send this ${definitionKind} version back to draft for rework. This does not delete it.`,
                        confirmLabel: 'Reject',
                        risk: 'medium',
                        onConfirm: () => transitionVersion('reject'),
                      })}
                    >
                      Reject
                    </button>
                  ) : null}
                </>
              ) : <button type="button" disabled={isSubmitting || !canEdit} title={!canEdit ? `Requires ${editPermission}` : undefined} onClick={() => void createDefinition()}>Create definition</button>}
            </div>
          </>
        ) : (
          <table className="data-table">
            <thead><tr><th>Version</th><th>State</th><th>Created</th><th>Change Log</th><th>Created By</th><th /></tr></thead>
            <tbody>{versions.map((version) => (
              <tr key={version.id}>
                <td>{version.versionLabel}</td>
                <td><StatusBadge value={version.lifecycleState} /></td>
                <td>{new Date(version.createdAt).toLocaleString('en-GB')}</td>
                <td>n/a</td>
                <td>backend</td>
                <td><button type="button" onClick={() => viewVersion(version)}>View</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

// ─── Constants and helpers ────────────────────────────────────────────────────

export const supportedDocumentTypes = documentTypeOptions.map((option) => option.value as DocumentType);

const protocolOptions = ['REST', 'WEBHOOK', 'SFTP', 'KAFKA'].map((value) => ({ label: value, value }));

function createDefaultConnectorInstance(): ConnectorInstanceRecord {
  return {
    id: '', name: '', definitionId: '', protocolFamily: 'REST', capabilityType: 'OUTBOUND_DELIVERY',
    partnerConfigId: '', endpoint: '', ownership: { team: 'integration' },
    timeoutPolicy: { requestTimeoutMs: 5000 }, retryPolicy: { maxAttempts: 3, backoffMs: 250, strategy: 'EXPONENTIAL' },
    health: { status: 'UNKNOWN' }, config: {}, secretRefs: [], isActive: true, createdAt: '', updatedAt: '',
  };
}

function createConnectorJsonDrafts(value: ConnectorInstanceRecord): Record<string, string> {
  return {
    health: JSON.stringify(value.health, null, 2),
    config: JSON.stringify(value.config, null, 2),
    secretRefs: JSON.stringify(value.secretRefs, null, 2),
  };
}

function toPartnerCreatePayload(value: PartnerRecord): Omit<PartnerRecord, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    name: value.name, code: value.code, protocol: value.protocol, isActive: value.isActive,
    settings: value.settings, legalName: value.legalName, taxIdentifiers: value.taxIdentifiers,
    contacts: value.contacts, operationalStatus: value.operationalStatus,
    supportedProtocols: value.supportedProtocols, inboundCapabilities: value.inboundCapabilities,
    outboundCapabilities: value.outboundCapabilities, supportedMessageTypes: value.supportedMessageTypes,
    throughputLimits: value.throughputLimits, slaProfile: value.slaProfile,
    securityProfile: value.securityProfile, environmentProfile: value.environmentProfile,
    runtimeProfile: value.runtimeProfile,
  };
}

function toPartnerUpdatePayload(value: Partial<PartnerRecord>): Partial<Omit<PartnerRecord, 'id' | 'createdAt' | 'updatedAt'>> {
  return {
    name: value.name, code: value.code, protocol: value.protocol, isActive: value.isActive,
    settings: value.settings, expectedVersion: value.version, legalName: value.legalName,
    taxIdentifiers: value.taxIdentifiers, contacts: value.contacts,
    operationalStatus: value.operationalStatus, supportedProtocols: value.supportedProtocols,
    inboundCapabilities: value.inboundCapabilities, outboundCapabilities: value.outboundCapabilities,
    supportedMessageTypes: value.supportedMessageTypes, throughputLimits: value.throughputLimits,
    slaProfile: value.slaProfile, securityProfile: value.securityProfile,
    environmentProfile: value.environmentProfile, runtimeProfile: value.runtimeProfile,
  };
}

function toConnectorDefinitionCreatePayload(value: ConnectorDefinitionRecord): Omit<ConnectorDefinitionRecord, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    name: value.name, code: value.code, protocolFamily: value.protocolFamily,
    capabilityType: value.capabilityType, description: value.description,
    driverRef: value.driverRef, supportedAuthSchemes: value.supportedAuthSchemes, isActive: value.isActive,
  };
}

function toConnectorDefinitionUpdatePayload(value: Partial<ConnectorDefinitionRecord>): Partial<Omit<ConnectorDefinitionRecord, 'id' | 'createdAt' | 'updatedAt'>> {
  return {
    name: value.name, code: value.code, protocolFamily: value.protocolFamily,
    capabilityType: value.capabilityType, description: value.description,
    driverRef: value.driverRef, supportedAuthSchemes: value.supportedAuthSchemes, isActive: value.isActive,
  };
}

function toConnectorInstanceCreatePayload(value: ConnectorInstanceRecord): Omit<ConnectorInstanceRecord, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    name: value.name, definitionId: value.definitionId, protocolFamily: value.protocolFamily,
    capabilityType: value.capabilityType, partnerConfigId: value.partnerConfigId, endpoint: value.endpoint,
    ownership: value.ownership, timeoutPolicy: value.timeoutPolicy, retryPolicy: value.retryPolicy,
    health: value.health, config: value.config, secretRefs: value.secretRefs, isActive: value.isActive,
  };
}

function toConnectorInstanceUpdatePayload(value: Partial<ConnectorInstanceRecord>): Partial<Omit<ConnectorInstanceRecord, 'id' | 'createdAt' | 'updatedAt'>> {
  return {
    name: value.name, definitionId: value.definitionId, protocolFamily: value.protocolFamily,
    capabilityType: value.capabilityType, partnerConfigId: value.partnerConfigId, endpoint: value.endpoint,
    ownership: value.ownership, timeoutPolicy: value.timeoutPolicy, retryPolicy: value.retryPolicy,
    health: value.health, config: value.config, secretRefs: value.secretRefs, isActive: value.isActive,
  };
}

function toInboundPolicyPayload(value: InboundPolicyRecord): Omit<InboundPolicyRecord, 'id' | 'name' | 'createdAt' | 'updatedAt'> {
  return {
    partnerId: value.partnerId,
    tenantId: value.tenantId || undefined,
    flowKey: value.flowKey || undefined,
    protocol: value.protocol,
    authType: value.authType,
    secretRef: value.secretRef || undefined,
    signatureKeyId: value.signatureKeyId || undefined,
    algorithm: value.algorithm,
    allowedIps: value.allowedIps,
    allowedClockSkewSeconds: value.allowedClockSkewSeconds,
    replayWindowSeconds: value.replayWindowSeconds,
    isActive: value.isActive,
  };
}
